import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AddUserLayer = () => {
    const navigate = useNavigate();

    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentUserRole, setCurrentUserRole] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        fullname: '',
        password: '',
        role_id: '',
        status: 'active',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    // Get current user info and fetch roles
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                setCurrentUsername(userData.username || '');
                setCurrentUserRole(userData.role || '');
                // Fetch roles for dropdown (MasterLayout handles route protection)
                fetchRoles();
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles/active`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setRoles(data.data);
                // Set default role if available
                if (data.data.length > 0 && !formData.role_id) {
                    const defaultRole = data.data.find(r => r.slug === 'user') || data.data[0];
                    setFormData(prev => ({ ...prev, role_id: defaultRole.id }));
                }
            }
        } catch (err) {
            console.error('Failed to fetch roles:', err);
        } finally {
            setLoadingRoles(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        if (!apiUrl || !token) {
            setError('Missing API URL or authentication token');
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await fetch(`${apiUrl}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to create user');
            }

            if (data.success) {
                // Redirect to users list on success
                navigate('/users-list');
            } else {
                throw new Error(data.message || 'Failed to create user');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/users-list');
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                              
                                {/* Upload Image End */}
                                {error && (
                                    <div className="alert alert-danger mb-20" role="alert">
                                        {error}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="username"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Username <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                validationErrors.username ? 'is-invalid' : ''
                                            }`}
                                            id="username"
                                            name="username"
                                            placeholder="Enter username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.username && (
                                            <div className="invalid-feedback">
                                                {validationErrors.username[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="fullname"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Full Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                validationErrors.fullname ? 'is-invalid' : ''
                                            }`}
                                            id="fullname"
                                            name="fullname"
                                            placeholder="Enter Full Name"
                                            value={formData.fullname}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.fullname && (
                                            <div className="invalid-feedback">
                                                {validationErrors.fullname[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="password"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Password <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control radius-8 ${
                                                validationErrors.password ? 'is-invalid' : ''
                                            }`}
                                            id="password"
                                            name="password"
                                            placeholder="Enter password (min 6 characters)"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.password && (
                                            <div className="invalid-feedback">
                                                {validationErrors.password[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="role_id"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Role <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                validationErrors.role_id ? 'is-invalid' : ''
                                            }`}
                                            id="role_id"
                                            name="role_id"
                                            value={formData.role_id}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loadingRoles}
                                        >
                                            <option value="">Select Role...</option>
                                            {(() => {
                                                // Find Asset Manager role in the roles array to determine the correct slug/name
                                                const assetManagerRole = roles.find(r => {
                                                    const slug = (r.slug || '').toLowerCase().trim();
                                                    const name = (r.name || '').toLowerCase().trim();
                                                    return slug === 'asset-manager' || 
                                                           slug === 'asset_manager' ||
                                                           name === 'asset manager';
                                                });
                                                
                                                // Determine if current user is Asset Manager
                                                const userRole = (currentUserRole || '').toString();
                                                const userRoleLower = userRole.toLowerCase().trim();
                                                const normalizedUserRole = userRoleLower.replace(/[_\s]/g, '-');
                                                
                                                // Check if user is Asset Manager by comparing with role slug/name
                                                let isAssetManager = false;
                                                if (assetManagerRole) {
                                                    const managerSlug = (assetManagerRole.slug || '').toLowerCase().trim();
                                                    const managerName = (assetManagerRole.name || '').toLowerCase().trim();
                                                    isAssetManager = normalizedUserRole === managerSlug.replace(/[_\s]/g, '-') ||
                                                                   userRoleLower === managerName ||
                                                                   userRoleLower === managerSlug ||
                                                                   normalizedUserRole === 'asset-manager';
                                                } else {
                                                    // Fallback: check common variations
                                                    isAssetManager = normalizedUserRole === 'asset-manager' ||
                                                                   userRoleLower === 'asset manager' ||
                                                                   userRoleLower === 'asset_manager';
                                                }
                                                
                                                // Find Asset User role
                                                const assetUserRole = roles.find(r => {
                                                    const slug = (r.slug || '').toLowerCase().trim().replace(/[_\s]/g, '-');
                                                    const name = (r.name || '').toLowerCase().trim();
                                                    return slug === 'asset-user' || 
                                                           slug === 'asset_user' ||
                                                           name === 'asset user';
                                                });
                                                
                                                // Filter roles based on user type
                                                return roles
                                                    .filter((role) => {
                                                        // Super admin (username "admin") can select Admin role
                                                        if (role.slug === 'admin' || role.name === 'Admin') {
                                                            return currentUsername === 'admin';
                                                        }
                                                        
                                                        // Asset Manager can ONLY select Asset User role
                                                        if (isAssetManager && assetUserRole) {
                                                            return role.id === assetUserRole.id;
                                                        }
                                                        
                                                        // For admin users, show all other roles
                                                        return true;
                                                    })
                                                    .map((role) => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.name}
                                                        </option>
                                                    ));
                                            })()}
                                        </select>
                                        {validationErrors.role_id && (
                                            <div className="invalid-feedback">
                                                {validationErrors.role_id[0]}
                                            </div>
                                        )}
                                        {loadingRoles && (
                                            <small className="text-secondary-light">Loading roles...</small>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="status"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Status <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                validationErrors.status ? 'is-invalid' : ''
                                            }`}
                                            id="status"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {validationErrors.status && (
                                            <div className="invalid-feedback">
                                                {validationErrors.status[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default AddUserLayer;